import React from 'react';
import { AccommodationCombined } from '../../Accommodations/Accommodation.types';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useTranslation } from '@shared/core';
import { Box, BoxProps, Flex, JoyKitThemeProvider, TextV2, TooltipV2 } from '@withjoy/joykit';
import { Skeleton, SkeletonGroup } from '@shared/components/Skeleton';
import { useTravelPlacesPricingAndBookingDetailsFragment } from '@shared/hooks/useTravelPlacesPricingAndBookingDetails';
import { useTravelPlacesPriceComparisonFragment } from '@shared/hooks/useTravelPlacesPriceComparison';
import { Info } from '@withjoy/joykit/icons';
import { JoySmallIcon } from '@assets/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { PercentageOffBadge } from './PercentageOffBadge';
import { IsoCurrencyIndicator } from './JoyPlacePricing';
import { CompareToPrice } from './CompareToPrice';
import { EventVenue } from '@shared/utils/eventPlace';

type JoyPlacePricingV2Props = { accommodation: AccommodationCombined; locationInfo?: EventVenue; isGridView?: boolean; hideComparison?: boolean } & BoxProps;

export const JoyPlacePricingV2 = ({ accommodation, locationInfo, isGridView, hideComparison = false, ...boxProps }: JoyPlacePricingV2Props) => {
  const { eventInfo } = useEventInfo();
  const eventDisplayName = eventInfo?.eventDisplayName;
  const { t: accommodationsTrans } = useTranslation('guestSiteCustom');
  const accTrans = accommodationsTrans('accommodations');

  const { price, percentageOff, loading: isTravelPlacesPricingAndBookingLoading } = useTravelPlacesPricingAndBookingDetailsFragment(accommodation.id, 'accommodationPlace');

  const { rates, loading: isPriceComparisonLoading } = useTravelPlacesPriceComparisonFragment(accommodation.joyPlaceId!, !accommodation.joyPlaceId || hideComparison);

  const loading = isTravelPlacesPricingAndBookingLoading || isPriceComparisonLoading;

  return (
    ((price || loading) && (
      <JoyKitThemeProvider>
        <Box {...boxProps}>
          <SkeletonGroup
            isReady={!loading}
            placeholder={
              <Flex justifyContent={isGridView ? 'flex-end' : 'space-between'} {...(!isGridView ? { borderTop: '1px solid', borderTopColor: 'mono3', paddingTop: 4 } : {})}>
                {!isGridView && (
                  <Skeleton
                    placeholder={
                      <Box width="7rem" height={pxToRem(16)} backgroundColor="mono3">
                        &nbsp;
                      </Box>
                    }
                  />
                )}
                <Flex flexDirection="column" gap={1} alignItems="flex-end">
                  {/* Event only Rate label */}
                  <Skeleton
                    placeholder={
                      <Box width="5rem" height={pxToRem(14)} backgroundColor="mono3">
                        &nbsp;
                      </Box>
                    }
                  />
                  {/* Price */}
                  <Skeleton
                    placeholder={
                      <Box width="4rem" height={pxToRem(18)} backgroundColor="mono3">
                        &nbsp;
                      </Box>
                    }
                  />
                </Flex>
              </Flex>
            }
          >
            {price && (
              <Box color="black">
                <Flex alignItems="center" justifyContent="space-between" gap={1}>
                  <TextV2 typographyVariant="body2" color="mono14" fontWeight={600} letterSpacing="-0.15px">
                    {accTrans.specialEventRate()}
                  </TextV2>
                  <Flex alignItems="center" justifyContent="flex-end" gap={1}>
                    {percentageOff && <PercentageOffBadge percentage={percentageOff} />}
                    {eventDisplayName ? (
                      <TooltipV2
                        content={accTrans.eventOnlyRateInfo({ name: eventDisplayName })}
                        onMouseEnterDelay={500}
                        placement="topEnd"
                        overrides={{
                          Content: { props: { backgroundColor: 'white', color: 'mono14', maxWidth: pxToRem(256), typographyVariant: 'label2' } },
                          Arrow: { props: { backgroundColor: 'white' } }
                        }}
                      >
                        <Info size="sm" color="accentGreen3" title={undefined} cursor="pointer" />
                      </TooltipV2>
                    ) : null}
                  </Flex>
                </Flex>
                <Flex flexDirection="column" alignItems="center" justifyContent="center" gap={2} marginTop={pxToRem(8)}>
                  <Flex alignItems="center" justifyContent="space-between" width="100%">
                    <Flex alignItems="center" justifyContent="center" gap={pxToRem(8)}>
                      <JoySmallIcon />
                      <TextV2 color="black" whiteSpace="nowrap" fontSize={pxToRem(15)}>
                        {accTrans.joyTravel()}
                      </TextV2>
                    </Flex>
                    <Flex typographyVariant="hed4" alignItems="center" justifyContent="flex-end" letterSpacing="-0.27px" width="100%">
                      <IsoCurrencyIndicator />
                      {accTrans.pricePerNight({ price })}
                    </Flex>
                  </Flex>
                  {!hideComparison && <CompareToPrice accommodation={accommodation} locationInfo={locationInfo} rates={rates} />}
                </Flex>
              </Box>
            )}
          </SkeletonGroup>
        </Box>
      </JoyKitThemeProvider>
    )) ||
    null
  );
};
